import {
  Box,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { COLORS } from "../../assets/theme";
import CustomButton from "../../components/common/CustomButton";
import AutoComplete from "../../components/common/AutoComplete";
import CustomSwitch from "../../components/common/CustomSwitch";
import FileUploadField from "../../components/FileUploadField";
import ImportForm from "../../components/ImportForm";
import ToastNotification from "../../components/ToastNotification";
import config from "../../config.json";
import {
  RoutingPlan,
  TextFields,
  uploadRoutingPlanExcel,
} from "../../services/ImportRateRouteService";
import { fetchRoutingPlans } from "../../services/RoutingPlanService";
import CustomTextField from "../../components/common/CustomTextField";
import CustomLabel from "../../components/common/CustomLabel";
import { IExport, useImportedFilesQuery } from "../../services/CostPlanService";
import { useQueryClient } from "@tanstack/react-query";
import Actions from "../../components/common/Actions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ErrorIcon from "@mui/icons-material/Error";
import CustomTable from "../../components/common/table/CustomTable";
import { IField, Params } from "../../types/table/interfaces.type";
import {
  ExportAttachmentApi,
  ExportErrorApi,
  IErrors,
} from "../../services/ExportService";
import CustomDialog from "../../components/common/CustomDialog";
import GetErrors from "../../components/GetErrors";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const ImportRoutingPlanPage: React.FC = () => {
  const [routingPlan, setRoutingPlan] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [removeAll, setRemoveAll] = useState<boolean>(false);
  const [routingPlans, setRoutingPlans] = useState<RoutingPlan[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [textFields, setTextFields] = useState<TextFields>({
    provider: "Provider",
    percentage: "",
    provider1: "",
    percentage1: "",
    provider2: "",
    percentage2: "",
    provider3: "",
    percentage3: "",
    mcc: "MCC",
    mnc: "MNC",
    rate: "Rate",
    effectiveDate: "Effective Date",
    countryName: "CountryName",
    operatorName: "OperatorName",
  });

  const [appliedFilters, setAppliedFilters] = useState<Params<IExport>>({
    usedBy: "RATE",
  });
  const queryClient = useQueryClient();

  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
  const [selectedExport, setSelectedExport] = useState<IExport | null>(null);
  const [confirmNoDateOpen, setConfirmNoDateOpen] = useState<boolean>(false);

  const actions = Actions({
    onExportAttem: {
      handler: (row: any) => {
        console.log(" atteem ", row);
        ExportAttachmentApi(row.attachmentId, row.usedBy);
      },
      isDisabled: !hasJwtPermission("DOWNLOAD_RATE_ROUTE_FILE"),
    },

    onView: {
      handler: (row: any) => {
        setSelectedExport(row as IExport);
        setViewDialogOpen(true);
      },
      isDisabled: !hasJwtPermission("GET_ALL_RATE_ROUTE_IMPORT_ERRORS"),
    },
  });

  const fields: IField<IExport>[] = [
    { key: "fileName", header: "File Name" },
    { key: "createdBy", header: "Created By" },
    { key: "createdDate", header: "Created Date" },
    {
      key: "status",
      header: "Status",
      transform: (row: IExport) => {
        switch (row.status?.toLowerCase()) {
          case "success":
            return (
              <Tooltip title="Full Import succeeded">
                <CheckCircleIcon sx={{ color: "green" }} />
              </Tooltip>
            );
          case "fail":
          case "failed":
            return (
              <Tooltip title="All entries failed">
                <CancelIcon sx={{ color: "red" }} />
              </Tooltip>
            );
          case "pending":
            return (
              <Tooltip title="Pending">
                <HourglassEmptyIcon sx={{ color: "orange" }} />
              </Tooltip>
            );
          case "errors":
            return (
              <Tooltip title="Errors occurred">
                <ErrorIcon sx={{ color: "darkorange" }} />
              </Tooltip>
            );
          default:
            return row.status;
        }
      },
    },
  ];
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const ErrorField: IField<IErrors>[] = [
    { key: "rowNumber", header: "Row Number" },
    { key: "errorMessage", header: "Error Message" },
    { key: "createdBy", header: "Created By" },
    { key: "createdDate", header: "Created Date" },
  ];

  useEffect(() => {
    const loadRoutingPlans = async () => {
      try {
        const routingPlansPage = await fetchRoutingPlans({
          size: config.MAX_PAGE_SIZE,
        });
        const routingPlansData = routingPlansPage.content;
        setRoutingPlans(
          Array.isArray(routingPlansData) ? routingPlansData : []
        );
      } catch (err) {
        ToastNotification.error("Failed to fetch routing plans.");
      }
    };

    loadRoutingPlans();
  }, []);

  const handleFileChange = (file: File | null) => {
    setFile(file);
  };

  const performUpload = async () => {
    try {
      setIsUploading(true);
      await uploadRoutingPlanExcel(routingPlan, file!, removeAll, textFields);
      ToastNotification.success("File uploaded successfully!");
      queryClient.invalidateQueries({ queryKey: ["importedFiles"] });
    } catch (err: any) {
      ToastNotification.error(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!routingPlan) {
      ToastNotification.error("Please select a routing plan.");
      return;
    }

    if (!file) {
      ToastNotification.error("Please select an Excel File file.");
      return;
    }

    if (!textFields.effectiveDate.trim()) {
      setConfirmNoDateOpen(true);
      return;
    }

    await performUpload();
  };

  const formStyles = {
    padding: { xs: 2, sm: 5 },
    borderRadius: "12px",
    backgroundColor: "#fff",
  };

  const additionalComponents = [
    <Box
      key="row-1"
      display="flex"
      alignItems={"end"}
      flexDirection={{ xs: "column", sm: "row" }}
      gap={2}
      sx={{ marginTop: 2 }}
    >
      <Box flex={{ xs: "100%", sm: "48%" }}>
        <AutoComplete
          label="Routing Plan"
          value={routingPlan}
          highlightLabelStar={true}
          sx={{ "& .MuiInputBase-root": { backgroundColor: COLORS.WhiteBackground } }}
          options={routingPlans.map((plan) => ({
            label: plan.name,
            value: plan.id,
          }))}
          placeholder="Select one"
          onChange={(value) => setRoutingPlan(value as string)}
        />
      </Box>
      <Box flex={{ xs: "100%", sm: "48%" }}>
        <FileUploadField
          placeholder="Add Excel file"
          accept=".xlsx,.xls,.csv"
          popupImageUrl="/RoutingPlan.png"
          popupImageAlt="Routing Plan Excel File"
          // showQuestionIcon={true}
          onFileChange={handleFileChange}
        />
      </Box>
    </Box>,
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
        Excel Header Fields
      </Typography>
    </Box>,
    <Grid container spacing={2} sx={{ width: "100%" }} key="text-fields-row">
      <Grid item xs={12} sm={6} md={2} component={"div" as React.ElementType}>
        <CustomTextField
          label="Provider Header"
          placeholder="Provider"
          size="small"
          highlightLabelStar
          fullWidth
          bgColor={COLORS.WhiteBackground}
          value={textFields.provider}
          onChange={(e) =>
            setTextFields({ ...textFields, provider: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType} xs={12} sm={6} md={2}>
        <CustomTextField
          label="Percentage Header"
          placeholder="Percentage"
          size="small"
          fullWidth
          bgColor={COLORS.WhiteBackground}
          value={textFields.percentage}
          onChange={(e) =>
            setTextFields({ ...textFields, percentage: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType} xs={12} sm={6} md={2}>
        <CustomTextField
          label="Provider1 Header"
          placeholder="Provider1"
          size="small"
          fullWidth
          bgColor={COLORS.WhiteBackground}
          value={textFields.provider1}
          onChange={(e) =>
            setTextFields({ ...textFields, provider1: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType} xs={12} sm={6} md={2}>
        <CustomTextField
          label="Percentage1 Header"
          placeholder="Percentage1"
          size="small"
          fullWidth
          bgColor={COLORS.WhiteBackground}
          value={textFields.percentage1}
          onChange={(e) =>
            setTextFields({ ...textFields, percentage1: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType} xs={12} sm={6} md={2}>
        <CustomTextField
          label="Provider2 Header"
          placeholder="Provider2"
          size="small"
          fullWidth
          bgColor={COLORS.WhiteBackground}
          value={textFields.provider2}
          onChange={(e) =>
            setTextFields({ ...textFields, provider2: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType} xs={12} sm={6} md={2}>
        <CustomTextField
          label="Percentage2 Header"
          placeholder="Percentage2"
          size="small"
          fullWidth
          bgColor={COLORS.WhiteBackground}
          value={textFields.percentage2}
          onChange={(e) =>
            setTextFields({ ...textFields, percentage2: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType} xs={12} sm={6} md={2}>
        <CustomTextField
          label="Provider3 Header"
          placeholder="Provider3"
          size="small"
          fullWidth
          bgColor={COLORS.WhiteBackground}
          value={textFields.provider3}
          onChange={(e) =>
            setTextFields({ ...textFields, provider3: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType} xs={12} sm={6} md={2}>
        <CustomTextField
          label="Percentage3 Header"
          placeholder="Percentage3"
          size="small"
          fullWidth
          bgColor={COLORS.WhiteBackground}
          value={textFields.percentage3}
          onChange={(e) =>
            setTextFields({ ...textFields, percentage3: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType} xs={12} sm={6} md={2}>
        <CustomTextField
          label="MCC Header"
          placeholder="MCC"
          size="small"
          fullWidth
          highlightLabelStar
          bgColor={COLORS.WhiteBackground}
          value={textFields.mcc}
          onChange={(e) =>
            setTextFields({ ...textFields, mcc: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType} xs={12} sm={6} md={2}>
        <CustomTextField
          label="MNC Header"
          placeholder="MNC"
          size="small"
          highlightLabelStar
          fullWidth
          bgColor={COLORS.WhiteBackground}
          value={textFields.mnc}
          onChange={(e) =>
            setTextFields({ ...textFields, mnc: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType} xs={12} sm={6} md={2}>
        <CustomTextField
          label="Rate Header"
          placeholder="Rate"
          size="small"
          highlightLabelStar
          fullWidth
          bgColor={COLORS.WhiteBackground}
          value={textFields.rate}
          onChange={(e) =>
            setTextFields({ ...textFields, rate: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType} xs={12} sm={6} md={2}>
        <CustomTextField
          label="Effective Date Header"
          placeholder="Effective Date"
          size="small"
          fullWidth
          highlightLabelStar
          bgColor={COLORS.WhiteBackground}
          value={textFields.effectiveDate}
          onChange={(e) =>
            setTextFields({ ...textFields, effectiveDate: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType} xs={12} sm={6} md={2}>
        <CustomTextField
          label="Operator Header"
          placeholder="OperatorName"
          size="small"
          fullWidth
          bgColor={COLORS.WhiteBackground}
          value={textFields.operatorName}
          onChange={(e) =>
            setTextFields({ ...textFields, operatorName: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType} xs={12} sm={6} md={2}>
        <CustomTextField
          label="Country Name Header"
          placeholder="CountryName"
          size="small"
          fullWidth
          bgColor={COLORS.WhiteBackground}
          value={textFields.countryName}
          onChange={(e) =>
            setTextFields({ ...textFields, countryName: e.target.value })
          }
        />
      </Grid>
    </Grid>,
    <Box
      key="row-2"
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems="center"
      gap={2}
      sx={{ marginTop: 2 }}
    >
      <Box flex={{ xs: "50%", sm: "48%" }}>
        <CustomSwitch
          label="Remove all items in the routing plan"
          checked={removeAll}
          onChange={(event) => setRemoveAll(event.target.checked)}
          activeText="ON"
          inactiveText="OFF"
          fontWeight="300"
          sx={{ mt: 3 }}
          fontSize="0.875rem"
        />
      </Box>
      <Box flex={{ xs: "50%", sm: "48%" }}>
        <CustomButton
          size="small"
          variant="contained"
          label={"Upload"}
          textColor={COLORS.textPrimary}
          bgColor={COLORS.GOLD}
          onClick={handleUpload}
          sx={{
            mt: 1,
            ml: "auto",
          }}
          permission="IMPORT_ROUTING_PLAN"
        />
      </Box>
    </Box>,
  ];

  return (
    <>
      <Box m={2} ml={{ xs: 2, sm: 3 }} mr={{ xs: 2, sm: 3 }}>
        <CustomLabel
          text="Import Rate/Route"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <ImportForm
          additionalComponents={additionalComponents}
          gap={3}
          layout="column"
          sx={formStyles}
        />

        <Box mt={2}>
          <CustomLabel
            text="Rate/Route Sessions List"
            variant={isSmallScreen ? "h6" : "h5"}
            fontWeight={500}
            marginTop={1}
            marginBottom={1}
          />
        </Box>
        <Box mt={1}>
          <CustomTable<IExport>
            filters={appliedFilters}
            fields={fields}
            fetchFunc={useImportedFilesQuery}
            actions={actions}
            sortableFields={["status", "createdDate", "createdBy"]}
          />
        </Box>
      </Box>
      <CustomDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        title="View Error Details"
        mode="history"
        fields={[
          {
            type: "custom",
            component: (
              <GetErrors
                usedBy="RATE"
                fields={ErrorField}
                exportId={String(selectedExport)}
              />
            ),
          },
        ]}
        buttons={[{ label: "Close", onClick: () => setViewDialogOpen(false) }]}
      />

      <CustomDialog
        open={confirmNoDateOpen}
        onClose={() => setConfirmNoDateOpen(false)}
        title="Missing Effective Date Header"
        mode="confirm"
        fields={[
          {
            type: "custom",
            component: (
              <Typography variant="body2">
                The <strong>Effective Date Header</strong> field is empty. Proceeding will import
                the file without mapping an effective date column. Are you sure you want to
                continue?
              </Typography>
            ),
          },
        ]}
        buttons={[
          {
            label: "Proceed",
            onClick: async () => {
              setConfirmNoDateOpen(false);
              await performUpload();
            },
          },
        ]}
      />
    </>
  );
};

export default ImportRoutingPlanPage;
